/**
 * Historia 176 — CU4, T5 (S97-S104, `S97-impacto-de-un-texto-jupiter.feature`). Pure computation
 * of a key's impact over the build artifact: `03-dominio.md` §6/§6.1 (T17-T19), `02-reglas.md`
 * RN7 (every literal use, not only labels), RN8 (screens), RN8b (groups, no product table), RN8d
 * (common structure apart, never adding to N or M), RN8e ("N screens in M modules", ordered).
 *
 * `artifact` is exactly the shape `buildArtifact.js` (the build-time analyzer) produces:
 * `keyScreens` (key → screen ids), `screenNames` (screen id → `{ keys, literal?, route, englishName }`),
 * `screenGroup` (screen id → group code), `groups` (group code → `{ nameKey, unnamed }`),
 * `commonStructureKeys` (keys used in the top bar/left menu/main container, RN8d) and
 * `commonStructureSites` (key → ordered site name keys of the frame, RN8h, DC-26).
 */

import { GROUP_ORDER } from '../../../buildScripts/languageCatalogueAnalyzer/groupNames'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

// RN8b/B11: the canonical menu order is the analyzer's own `GROUP_ORDER` (`groupNames.js`), the
// single source: a second list here diverged before. A group whose code is not in it sorts after
// all listed ones, never breaking the render.
const menuOrderIndexOf = (code) => {
  const index = GROUP_ORDER.indexOf(code)
  return index === -1 ? GROUP_ORDER.length : index
}

/**
 * @param {string} key The i18n key
 * @param {object} artifact The build artifact (toy or real, same shape)
 * @param {object} systemValues Story 204: `indexSystemValues(...)` of C5; a system value's impact is global (RN6.4):
 *   no screens are listed and `systemValue: true` (with the domains it appears in) is returned instead
 * @returns {{screens: number, modules: number, groups: Array<{code: string, nameKey: string, screens: Array<{id: string, keys: string[], literal: string, route: string, englishName: string}>}>, commonStructure: boolean, commonSites: string[], systemValue: boolean, domains: string[]}}
 */
export const impactOf = (key, artifact = {}, systemValues = NO_SYSTEM_VALUES) => {
  if (systemValues.has(key)) {
    return { screens: 0, modules: 0, groups: [], commonStructure: false, commonSites: [], systemValue: true, domains: systemValues.domainsOf(key) }
  }
  const keyScreens = artifact.keyScreens || {}
  const screenNames = artifact.screenNames || {}
  const screenGroup = artifact.screenGroup || {}
  const groups = artifact.groups || {}
  const commonStructureKeys = artifact.commonStructureKeys || []
  const commonStructureSites = artifact.commonStructureSites || {}

  // RN7: every literal use counts (buildArtifact already put every one here, label or not).
  const screenIds = keyScreens[key] || []
  const commonStructure = commonStructureKeys.includes(key)

  const byGroup = {}
  for (const screenId of screenIds) {
    const groupCode = screenGroup[screenId]
    if (groupCode === undefined) continue
    if (!byGroup[groupCode]) {
      const groupInfo = groups[groupCode] || {}
      byGroup[groupCode] = { code: groupCode, nameKey: groupInfo.nameKey || groupCode, screens: [] }
    }
    const screenInfo = screenNames[screenId] || {}
    byGroup[groupCode].screens.push({
      id: screenId,
      keys: screenInfo.keys || [],
      literal: screenInfo.literal || '',
      route: screenInfo.route || '',
      englishName: screenInfo.englishName || screenId
    })
  }

  // RN8e: groups ordered as in the menu, screens within a group ordered by name.
  const orderedGroups = Object.values(byGroup)
    .sort((a, b) => menuOrderIndexOf(a.code) - menuOrderIndexOf(b.code))
    .map((group) => ({
      ...group,
      screens: [...group.screens].sort((s1, s2) => s1.englishName.localeCompare(s2.englishName))
    }))

  return {
    screens: screenIds.length,
    modules: orderedGroups.length,
    groups: orderedGroups,
    commonStructure,
    // RN8h/DC-26: the ordered site name keys of the frame where the key is used (none if the artifact has no entry).
    commonSites: commonStructure ? commonStructureSites[key] || [] : [],
    systemValue: false,
    domains: []
  }
}

/**
 * RN9/DG1-6 DP-3: guarding a save/delete/restore that would be seen in more than one module
 * (S105), or that is also in the common structure — always, regardless of how many modules
 * (S106). Story 204 (RN6.4): a system value always asks, whatever its screens.
 *
 * @param {{modules: number, commonStructure: boolean, systemValue?: boolean}} impact `impactOf(...)` result
 * @returns {boolean}
 */
export const needsConfirmation = (impact) => impact.systemValue === true || impact.modules >= 2 || impact.commonStructure === true
